/**
 * The team form's window onto אנשי חוץ.
 *
 * Reads the directory once, re-reads it whenever the assignment domain
 * announces a write, and hands back the one action the form needs: keep this
 * person for next time. The store is what enforces the permission and the
 * separation from the two rosters — this is only the wiring.
 */
import { useCallback, useEffect, useState } from 'react';
import { getExternalPeople, rememberExternalPerson } from '../../../db/external-people-store';
import { useStoreEvents } from '../../../hooks/use-store-events';
import type { CrewRole, ExternalPerson, ExternalPersonDraft } from '../../../types/assignment';
import type { ExternalPersonRecord } from '../../../types/external-person';
import type { User } from '../../../types/user';

export interface ExternalPeopleHandle {
  /** The kept cards, most recently placed first. */
  people: ExternalPersonRecord[];
  /** True until the first read has come back. */
  loading: boolean;
  /**
   * Files one person under אנשי חוץ, or updates the card they already have.
   * A caller with no signed-in user gets `null` and no write — the form is
   * still perfectly usable, the person simply stays inside this mission.
   */
  remember: (
    person: ExternalPerson | ExternalPersonDraft,
    crewRole?: CrewRole,
  ) => Promise<ExternalPersonRecord | null>;
}

export function useExternalPeople(user?: User | null): ExternalPeopleHandle {
  const [people, setPeople] = useState<ExternalPersonRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setPeople(await getExternalPeople());
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useStoreEvents(load, ['assignments']);

  const remember = useCallback<ExternalPeopleHandle['remember']>(
    async (person, crewRole) => {
      if (!user) return null;
      const saved = await rememberExternalPerson(user, person, crewRole);
      await load();
      return saved;
    },
    [user, load],
  );

  return { people, loading, remember };
}
